/**
 * What sharing one document hands over besides the page (RFC 0345 3d, S10).
 *
 * An embed goes with the page at the chosen level: a blank embed is worse
 * than none. An app's record drawn as an embed needs the app too, at Read
 * and never more: its registration and its view's code. Nothing else goes:
 * not a sibling record, not the project, not the app's skill, and not an
 * editor app that claims a page's type, since a guest's General never takes
 * another person's editor (the guest draws the page with their own).
 *
 * An embedded page's own embeds go too, all the way down, each once. The
 * window draws one level and shows deeper ones as chips, and a chip the
 * guest can open beats one that says no access. Until an embedded page has
 * been read its embeds are unknown, and the share waits rather than guess.
 *
 * Pure, so the share dialog can name every line of it before anything is
 * sent, and so a dependency it cannot name stops the send instead of
 * widening the grant quietly. `createInvite` checks each line again against
 * the documents themselves before anything is granted.
 */
import type { AppSummary } from '@mod/core/model'

type PageLevel = 'edit' | 'comment' | 'read'

/** A link alone on its line, the line the editor draws as an embed
 *  (`packages/editor/src/document-embeds.js`, `LONE_LINK`). */
const LONE_LINK = /^\[([^\]]*)\]\(([^()\s]+)\)$/

export interface EmbedSubject {
  id: string
  title: string
  path?: string
  mime?: string
  dataType?: string
  /** Set on an app's record: the app that draws it. */
  appId?: string
  /** A record whose app is not installed here: nothing can draw it. */
  missingApp?: string
}

export interface PlannedGrant {
  id: string
  /** What the dialog shows, and what `createInvite` checks against the
   * document: a page's title, an app's name, a view's path. */
  title: string
  why: 'embed' | 'app' | 'view'
  level: PageLevel
  /** An embed's path and type, checked against the document. */
  document?: { path: string; mime: string }
}

export type EmbedPlan =
  | { ok: true; grants: PlannedGrant[] }
  | { ok: false; reason: string }

export function planDocumentShare(options: {
  document: EmbedSubject
  /** The page's markdown, for its embeds. */
  content: string
  level: PageLevel
  resolve: (href: string) => EmbedSubject | null
  apps: readonly AppSummary[]
  /** A catalog document's path, for naming a view by what it is. */
  pathOf?: (id: string) => string | undefined
  /** An embedded page's own markdown, once read. Undefined while it is
   *  unread and null when it could not be read; both refuse: an embed's
   *  embeds are named or nothing goes. */
  contentOf?: (id: string) => string | null | undefined
}): EmbedPlan {
  const embeds = new Map<string, PlannedGrant>()
  const drawers = new Map<string, PlannedGrant>()
  const subjects = [options.document]
  // Every page reachable through embeds, each once, so a cycle ends.
  const pages = [options.content]
  while (pages.length) {
    const content = pages.shift() ?? ''
    for (const line of content.split('\n')) {
      const m = LONE_LINK.exec(line.trim())
      if (!m) continue
      const embed = options.resolve(m[2])
      // A lone link that names nothing here is a plain link, drawn as text.
      if (!embed || embed.id === options.document.id || embeds.has(embed.id))
        continue
      embeds.set(embed.id, {
        id: embed.id,
        title: embed.title,
        why: 'embed',
        level: options.level,
        document: {
          path: embed.path ?? embed.title,
          mime: embed.mime ?? 'text/markdown',
        },
      })
      subjects.push(embed)
      // A page that embeds pages: its own embeds go too, or the share waits.
      if (embed.appId || embed.missingApp) continue
      if ((embed.mime ?? 'text/markdown') !== 'text/markdown') continue
      const inner = options.contentOf?.(embed.id)
      if (inner === undefined)
        return {
          ok: false,
          reason: `Still reading what ${embed.title} embeds`,
        }
      if (inner === null)
        return {
          ok: false,
          reason: `Could not read ${embed.title}, so what it embeds is unknown`,
        }
      pages.push(inner)
    }
  }
  for (const subject of subjects) {
    if (subject.missingApp || (subject.appId && !drawerOf(subject, options)))
      return {
        ok: false,
        reason: `The app that draws ${subject.title} is not installed here`,
      }
    const app = drawerOf(subject, options)
    if (!app) continue
    const viewPath = app.viewDocId && options.pathOf?.(app.viewDocId)
    if (!app.viewDocId || !viewPath)
      return { ok: false, reason: `${app.name} has no view to share` }
    drawers.set(app.id, {
      id: app.id,
      title: app.name,
      why: 'app',
      level: 'read',
    })
    drawers.set(app.viewDocId, {
      id: app.viewDocId,
      title: viewPath,
      why: 'view',
      level: 'read',
    })
  }
  // An app's code is Read even where a page embeds the app itself.
  for (const id of drawers.keys()) embeds.delete(id)
  drawers.delete(options.document.id)
  return { ok: true, grants: [...embeds.values(), ...drawers.values()] }
}

const drawerOf = (
  subject: EmbedSubject,
  options: { apps: readonly AppSummary[] },
) =>
  subject.appId ? options.apps.find((a) => a.id === subject.appId) : undefined
